"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { blockedCells, findViolations } from "@engine/check";
import type { Hint } from "@engine/hints";
import type { SegmentRef } from "@engine/render";
import type { CellIdx } from "@engine/types";
import type { Walkthrough } from "@engine/walkthrough";
import type { PuzzleView } from "@/server/puzzles";
import { Link } from "@/i18n/routing";
import { objectName, themeFor } from "@/themes/registry";
import { AfterGame } from "./AfterGame";
import { BoardLegend } from "./BoardLegend";
import { applyMark, markFor, nextMark, nextSuspect, type BoardMarks } from "./cellState";
import { ClueList } from "./ClueList";
import { GuestProgressPrompt } from "./GuestProgressPrompt";
import { Grid } from "./Grid";
import { cellsForRef } from "./refCells";
import { Toolbar } from "./Toolbar";
import { WalkthroughPanel } from "./WalkthroughPanel";

interface Props {
  puzzle: PuzzleView;
  resumed?:
    | {
        placements: (number | null)[];
        notes: Record<string, number[]>;
        exclusions: Record<string, number[]>;
        hintsRevealed: number;
        clicks: number;
        elapsedMs: number;
      }
    | undefined;
  signedIn: boolean;
  actions: {
    save: (input: unknown) => Promise<{ saved: boolean }>;
    restart: (input: unknown) => Promise<{ restarted: boolean }>;
    hint: (input: unknown) => Promise<Hint | null>;
    submit: (input: unknown) => Promise<SubmitResultView | null>;
    rate: (input: unknown) => Promise<{ saved: boolean }>;
    report: (input: unknown) => Promise<{ filed: boolean; reason?: string }>;
    walkthrough: (input: unknown) => Promise<Walkthrough | null>;
  };
}

export interface SubmitResultView {
  correct: boolean;
  correctCount: number;
  total: number;
  murderer?: { name: string; letter: string };
  victim?: { name: string; letter: string };
  elapsedMs?: number;
  score?: number;
  clicks?: number;
}


export function Game({ puzzle, resumed, signedIn, actions }: Props) {
  const t = useTranslations("game");
  const tGift = useTranslations("gift");
  const locale = useLocale();
  // Resolved here rather than on the server: a theme is React components, which cannot cross the
  // server boundary as props. The id can.
  const theme = themeFor(puzzle.themeId);
  const characterCount = puzzle.characters.length;

  const [marks, setMarks] = useState<BoardMarks>(() => ({
    placements: resumed?.placements ?? new Array<number | null>(characterCount).fill(null),
    notes: resumed?.notes ?? {},
    exclusions: resumed?.exclusions ?? {},
  }));
  const [history, setHistory] = useState<BoardMarks[]>([]);
  const [selected, setSelected] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(resumed?.elapsedMs ?? 0);
  const [hintsRevealed, setHintsRevealed] = useState(resumed?.hintsRevealed ?? 0);
  const [clicks, setClicks] = useState(resumed?.clicks ?? 0);
  const [hint, setHint] = useState<Hint | null>(null);
  const [result, setResult] = useState<SubmitResultView | null>(null);
  /** What a clue is pointing at (GAME-7), and what a walkthrough step is pointing at (GAME-11). */
  const [pointedRef, setPointedRef] = useState<SegmentRef | null>(null);
  const [pointedCells, setPointedCells] = useState<CellIdx[]>([]);

  /**
   * The clock starts on the first move, not on arrival.
   *
   * Reading the clues before touching anything is playing the game; being charged for the time it
   * takes to read them is not. It also means opening a case to look at it costs nothing.
   */
  const started = clicks > 0 || elapsedMs > 0;

  // It runs only while the tab is visible, so "time taken" means time actually spent.
  useEffect(() => {
    if (!started || result?.correct) return;
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") setElapsedMs((ms) => ms + 1000);
    }, 1000);
    return () => clearInterval(tick);
  }, [started, result]);

  /**
   * Is there anything worth keeping? An untouched board is not progress, and saving one would
   * hand a guest cookie to somebody who has only looked at the page — and, worse, let two
   * near-simultaneous first saves each mint their own guest id before either cookie has landed.
   */
  const worthSaving =
    clicks > 0 ||
    hintsRevealed > 0 ||
    marks.placements.some((cell) => cell !== null) ||
    Object.keys(marks.notes).length > 0 ||
    Object.keys(marks.exclusions).length > 0;

  // Save shortly after every change. Notes must never be lost.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!worthSaving) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const state = { ...marks, hintsRevealed, clicks };
      // Guest or member, the game lives on the server: a guest's row is keyed on their browser and
      // moves onto the account if they sign up, so nobody plays into something that can vanish.
      void actions.save({ puzzleId: puzzle.id, state, elapsedMs, clicks });
    }, 600);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [marks, elapsedMs, hintsRevealed, clicks, worthSaving, actions, puzzle.id]);

  // Undo needs the state we are leaving, not the one we are moving to.
  const currentRef = useRef(marks);
  useEffect(() => {
    currentRef.current = marks;
  }, [marks]);

  const commit = useCallback((next: BoardMarks) => {
    // Read the ref NOW, not inside the updater. State updaters run lazily at render time, by which
    // point currentRef has already moved on — history would record the state we moved TO and undo
    // would quietly do nothing.
    const previous = cloneMarks(currentRef.current);
    currentRef.current = next;
    setHistory((past) => [...past.slice(-49), previous]);
    setMarks(next);
  }, []);

  const violations = useMemo(
    () =>
      findViolations({
        board: puzzle.board,
        characterCount,
        clues: puzzle.clues,
        generalClues: puzzle.generalClues,
        placements: marks.placements,
      }),
    [puzzle, characterCount, marks.placements],
  );

  const blocked = useMemo(
    () => blockedCells(puzzle.board, marks.placements),
    [puzzle.board, marks.placements],
  );

  const highlighted = useMemo(() => {
    const cells = new Set<CellIdx>(violations.flatMap((v) => v.cells));
    for (const cell of hint?.cells ?? []) cells.add(cell);
    if (hint?.cell !== undefined) cells.add(hint.cell);
    for (const cell of pointedCells) cells.add(cell);
    if (pointedRef) {
      for (const cell of cellsForRef(pointedRef, puzzle.board, marks.placements)) cells.add(cell);
    }
    return cells;
  }, [violations, hint, pointedRef, pointedCells, puzzle.board, marks.placements]);

  /**
   * Swiping sideways across the board changes the selected suspect — on a phone the suspect list
   * can be a screen away, and the thumb is already on the board. The gesture must never fight the
   * tap: only a clearly horizontal movement counts, and the click that the browser fires after a
   * swipe is swallowed. `touchAction: pan-y` on the wrapper keeps vertical scrolling native.
   */
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);

  const onBoardPointerDown = (event: React.PointerEvent): void => {
    swipedRef.current = false;
    swipeRef.current = { x: event.clientX, y: event.clientY };
  };

  const onBoardPointerUp = (event: React.PointerEvent): void => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || result?.correct) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= 2 * Math.abs(dy)) return;

    swipedRef.current = true;
    // Swipe left = next suspect, like turning a page forward.
    setSelected((current) => nextSuspect(current, dx < 0 ? 1 : -1, puzzle.characters));
  };

  /** The click a browser fires after a swipe would place a mark; a swipe is not a decision. */
  const onBoardClickCapture = (event: React.MouseEvent): void => {
    if (swipedRef.current) {
      event.preventDefault();
      event.stopPropagation();
      swipedRef.current = false;
    }
  };

  /**
   * Clicks are the efficiency measure, so cycling one square through its states must be free —
   * otherwise ruling a suspect out (which passes through "placed") would cost three times as much
   * as placing them. Consecutive taps on the same square are one decision, and count once.
   */
  const lastTapRef = useRef<{ cell: CellIdx; char: number } | null>(null);

  const tapCell = useCallback(
    (cell: CellIdx) => {
      if (result?.correct) return;

      const previous = lastTapRef.current;
      if (!previous || previous.cell !== cell || previous.char !== selected) {
        setClicks((n) => n + 1);
      }
      lastTapRef.current = { cell, char: selected };

      // Placing somewhere that already clashes with a taken row or column would only raise a
      // contradiction the player did not ask for, so the cycle skips it there.
      const canPlace = !blocked.has(cell) || marks.placements[selected] === cell;
      const next = nextMark(markFor(marks, selected, cell), canPlace);
      commit(applyMark(marks, selected, cell, next));
    },
    [marks, selected, blocked, commit, result],
  );

  const undo = (): void => {
    const previous = history[history.length - 1];
    if (!previous) return;
    currentRef.current = previous;
    setMarks(previous);
    setHistory((past) => past.slice(0, -1));
    lastTapRef.current = null;
  };

  /**
   * Start over.
   *
   * The attempt so far is kept on the server, marked abandoned — how far someone got before giving
   * up is worth knowing. What the player gets is a genuinely fresh start: no marks, no clicks, no
   * clock. Undo cannot reach back across it, because this is a new attempt and not a move.
   */
  const clearAll = (): void => {
    lastTapRef.current = null;
    setHistory([]);
    setClicks(0);
    setElapsedMs(0);
    setHintsRevealed(0);
    setHint(null);
    const empty: BoardMarks = {
      placements: new Array<number | null>(characterCount).fill(null),
      notes: {},
      exclusions: {},
    };
    currentRef.current = empty;
    setMarks(empty);
    void actions.restart({ puzzleId: puzzle.id });
  };

  const askForHint = async (): Promise<void> => {
    const received = await actions.hint({
      puzzleId: puzzle.id,
      placements: marks.placements,
      level: Math.min(hintsRevealed, 2),
    });
    if (!received) return;

    setHint(received);
    setHintsRevealed((n) => n + 1);
    if (received.code === "revealPlacement" && received.char !== undefined && received.cell !== undefined) {
      commit(applyMark(marks, received.char, received.cell, "placed"));
    }
  };

  const allPlaced = marks.placements.every((cell) => cell !== null);

  const submit = async (): Promise<void> => {
    const outcome = await actions.submit({
      puzzleId: puzzle.id,
      placements: marks.placements,
      elapsedMs,
      hintsRevealed,
      clicks,
    });
    if (outcome) setResult(outcome);
  };

  // A guest who has actually done something is worth offering an account to on the way out.
  const hasProgress =
    !signedIn &&
    (clicks > 0 || marks.placements.some((cell) => cell !== null) || Object.keys(marks.notes).length > 0);

  return (
    <div className="game-page">
      <GuestProgressPrompt hasProgress={hasProgress} />

      {/* Title and the case's measurements share a line: the size and level are worth knowing once,
          not worth a line of their own above a board that wants the height. */}
      <header className="case-header">
        <h1 style={{ margin: "0 0 0.15rem", fontSize: "clamp(1.4rem, 4vw, 2rem)" }}>
          {puzzle.title}{" "}
          <span className="case-meta" style={{ color: "var(--muted)", fontSize: "0.8em", fontWeight: 400 }}>
            {puzzle.size}×{puzzle.size} · {characterCount - 1} · {puzzle.tier.replace("_", " ")}
          </span>
        </h1>
        <p data-testid="case-question" style={{ margin: 0, fontSize: "1.05rem" }}>
          {puzzle.question}
        </p>
        {signedIn && (
          <Link className="gift-button" data-testid="gift-button" href={`/gift/new?case=${puzzle.slug}`}>
            {tGift("button")}
          </Link>
        )}
      </header>

      {result && <ResultPanel result={result} t={t} premiseKey={theme.premiseKey} />}

      {result?.correct && (
        <WalkthroughPanel
          premiseKey={theme.premiseKey}
          puzzleId={puzzle.id}
          placements={marks.placements}
          fetchWalkthrough={actions.walkthrough}
          onHighlightCells={setPointedCells}
        />
      )}

      {result?.correct && (
        <AfterGame
          puzzleId={puzzle.id}
          signedIn={signedIn}
          actions={{ rate: actions.rate, report: actions.report }}
          snapshot={{ placements: marks.placements, clicks, elapsedMs }}
        />
      )}

      <Toolbar
        premiseKey={theme.premiseKey}
        elapsedMs={elapsedMs}
        clicks={clicks}
        onUndo={undo}
        canUndo={history.length > 0}
        onClear={clearAll}
        onHint={() => void askForHint()}
        onSubmit={() => void submit()}
        canSubmit={allPlaced && !result?.correct}
        hintsRevealed={hintsRevealed}
      />

      {/* A count the tests can read: how many squares the board is pointing at right now. */}
      <span id="board-highlight-count" hidden>
        {highlighted.size}
      </span>

      {/*
        One strip, always the same height, for everything the board has to say.
        A contradiction and a hint used to be inserted above the board, so the board jumped down
        the moment either appeared and back up when it went — while the player was looking at it.
        They now take turns in a slot that is always there, holding the tap guidance when there is
        nothing more urgent, so nothing below it ever moves.
      */}
      <div className="board-status">
        {violations.length > 0 ? (
          <p data-testid="violation-warning" role="status" className="board-status-message is-warning">
            {t("contradiction")}
          </p>
        ) : hint ? (
          <HintPanel hint={hint} puzzle={puzzle} t={t} />
        ) : (
          <p data-testid="tap-hint" className="board-status-message is-quiet">
            {t("tapCycle")}
          </p>
        )}
      </div>

      {/* Columns come from globals.css, not from here: an inline grid-template-columns beats any
          stylesheet rule, which is what kept the side-by-side layout from ever applying. */}
      <div className="game-layout">
        <div
          className="board-fit"
          data-testid="board-swipe-area"
          onPointerDown={onBoardPointerDown}
          onPointerUp={onBoardPointerUp}
          onClickCapture={onBoardClickCapture}
          // Width is the grid track's business; only the gesture behaviour is set here.
          style={{ touchAction: "pan-y" }}
        >
          <Grid
            board={puzzle.board}
            characters={puzzle.characters}
            placements={marks.placements}
            notes={marks.notes}
            exclusions={marks.exclusions}
            blocked={blocked}
            highlighted={highlighted}
            selected={selected}
            roomNames={puzzle.roomNames}
            objectLabel={(kind) => objectName(theme, locale, kind)}
            theme={theme}
            onCellTap={tapCell}
          />
        </div>

        {/* The clue square: the same size as the board and beside it. Scrolls inside itself when
            a case has more clues than the square holds. */}
        <div className="side-column">
          <ClueList
            premiseKey={theme.premiseKey}
            puzzle={puzzle}
            selected={selected}
            onSelect={setSelected}
            placements={marks.placements}
            onHighlight={setPointedRef}
            active={pointedRef}
          />
        </div>

        {/* Last in the DOM, so a phone reads board → suspects → legend; on a wide screen it spans
            both squares instead, which is the width its flowing list wants. */}
        <div className="board-legend">
          <BoardLegend board={puzzle.board} theme={theme} />
        </div>
      </div>

    </div>
  );
}

const cloneMarks = (marks: BoardMarks): BoardMarks => ({
  placements: [...marks.placements],
  notes: { ...marks.notes },
  exclusions: { ...marks.exclusions },
});

type Translate = ReturnType<typeof useTranslations<"game">>;

function HintPanel({ puzzle, hint, t }: { puzzle: PuzzleView; hint: Hint; t: Translate }) {
  const name = hint.char !== undefined ? (puzzle.characters[hint.char]?.name ?? "") : "";
  const message =
    hint.code === "fixContradiction"
      ? t("hintContradiction")
      : hint.code === "allDone"
        ? t("hintAllDone")
        : hint.code === "lookAtClue"
          ? t("hintLook", { name })
          : hint.code === "narrowedTo"
            ? t("hintNarrowed", { name })
            : t("hintReveal", { name });

  return (
    <p data-testid="hint-message" role="status" className="board-status-message is-hint">
      {message}
    </p>
  );
}

function ResultPanel({
  result,
  t,
  premiseKey,
}: {
  result: SubmitResultView;
  t: Translate;
  premiseKey: string;
}) {
  const premise = useTranslations(`premises.${premiseKey}`);
  if (!result.correct) {
    return (
      <p data-testid="result-wrong" role="status" style={warningStyle}>
        {premise("wrong", { correct: result.correctCount, total: result.total })}
      </p>
    );
  }
  const minutes = Math.max(1, Math.round((result.elapsedMs ?? 0) / 60000));
  return (
    <div data-testid="result-correct" role="status" style={{ ...warningStyle, borderColor: "var(--accent)" }}>
      <strong>{t("solved")}</strong>{" "}
      {premise("solvedBody", { murderer: result.murderer?.name ?? "", victim: result.victim?.name ?? "" })}{" "}
      <span data-testid="result-minutes">{t("solvedIn", { minutes })}</span>{" "}
      {result.score !== undefined && (
        <span data-testid="result-score">
          {t("scored", { score: result.score, clicks: result.clicks ?? 0 })}
        </span>
      )}
    </div>
  );
}

// Longhand, because callers override borderColor alone when spreading this. Mixing the shorthand
// here with a longhand there leaves React unable to undo the override cleanly.
const warningStyle = {
  margin: 0,
  padding: "0.75rem 1rem",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "var(--accent)",
  borderRadius: "0.5rem",
  background: "rgba(140,28,19,0.06)",
} as const;
